import { useEffect, useMemo } from 'react';

import { usePageSectionContext } from '@/components/page-sections/page-section-context';
import type { FormField } from '@/components/shared/form/form-fields';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { useIsPreviewMode } from '@/hooks/use-is-preview-mode';
import { type ResourceForm, useResourcesStore } from '@/stores/resources-store';
import type { FormSettings } from '@/types/form';

export interface FormBlockResolvedForm {
  id: string;
  title?: string;
  description?: string;
  fields: FormField[];
  settings?: FormSettings;
  thirdParty?: boolean;
}

export function pickDefaultForm(forms: ResourceForm[]): ResourceForm | undefined {
  if (forms.length === 0) {
    return undefined;
  }

  return forms.find((form) => form.isDefault) ?? forms[0];
}

export function findFormByIdOrDefault(
  forms: ResourceForm[],
  formId?: string | null,
): ResourceForm | undefined {
  if (formId) {
    return forms.find((form) => form.id === formId);
  }
  return pickDefaultForm(forms);
}

export function resolveFormBlock({
  form,
  formId,
  forms,
  shouldResolveFromAccount,
  hasLoadedForms,
}: {
  form?: FormBlockResolvedForm;
  formId?: string | null;
  forms: ResourceForm[];
  shouldResolveFromAccount: boolean;
  hasLoadedForms: boolean;
}): FormBlockResolvedForm | undefined {
  if (shouldResolveFromAccount) {
    const selected = findFormByIdOrDefault(forms, formId);
    if (selected) {
      return {
        id: selected.id,
        title: selected.title,
        description: selected.description,
        fields: selected.fields,
        settings: selected.settings,
        thirdParty: selected.thirdParty,
      };
    }

    if (formId && hasLoadedForms) {
      return undefined;
    }
  }

  return form;
}

export function useResolvedFormBlock({
  form,
  formId,
}: {
  form?: FormBlockResolvedForm;
  formId?: string | null;
}): FormBlockResolvedForm | undefined {
  const isPageEditor = useIsPageEditor();
  const isPreviewMode = useIsPreviewMode();
  const sectionContext = usePageSectionContext();
  const isTemplatePreview = sectionContext?.renderMode === 'template_preview';
  const forms = useResourcesStore((state) => state.forms);
  const hasLoadedForms = useResourcesStore((state) => state.hasLoadedForms);
  const loadForms = useResourcesStore((state) => state.loadForms);

  const shouldResolveFromAccount =
    (Boolean(formId) || !form?.id) && (isPageEditor || isPreviewMode || isTemplatePreview);

  useEffect(() => {
    if (!shouldResolveFromAccount || hasLoadedForms) {
      return;
    }

    void loadForms();
  }, [hasLoadedForms, loadForms, shouldResolveFromAccount]);

  return useMemo(() => {
    return resolveFormBlock({
      form,
      formId,
      forms,
      shouldResolveFromAccount,
      hasLoadedForms,
    });
  }, [form, formId, forms, shouldResolveFromAccount, hasLoadedForms]);
}
